/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { useState } from 'react';
import Link from 'next/link';
import { useLanguage } from '@/providers/LanguageProvider';
import { Component } from 'types/api-data';

import { Skeleton } from '@/components/ui/skeleton';
import { Icons } from '@/components/icons';

import WorkflowRepoDialog from './workflow-repo-dialog';
import WorkflowRepoEdit from './workflow-repo-edit';

const WorkflowRepository = ({ component }: { component: Component }) => {
  const [repoUrl, setRepoUrl] = useState(
    component?.properties?.repository_url ?? ''
  );
  const [repoLoading, setRepoLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const { messages } = useLanguage();
  const generalIntl = messages?.General;
  const compIntl = messages?.ComponentsPage;

  return (
    <>
      {repoLoading ? (
        <Skeleton className="animate-pulse-custom mx-4 h-[60px] w-44 rounded-xl bg-[#8C9EA1]" />
      ) : (
        <div className="min-w-44 px-4">
          {repoUrl ? (
            <div className="group flex items-center gap-2.5">
              <div>
                <h3 className="pb-1 text-body-sm text-form-input-disabled">
                  {generalIntl?.source_control}
                </h3>
                <Link
                  href={repoUrl ?? ''}
                  className="flex items-center gap-1 hover:underline hover:underline-offset-2"
                  target="_blank"
                >
                  {Icons.Github('text-white')}
                  <p className="text-body text-body-primary">{repoUrl}</p>
                  {Icons.PopOut()}
                </Link>
              </div>

              <WorkflowRepoEdit
                component={component}
                setRepoUrl={setRepoUrl}
                setRepoLoading={setRepoLoading}
                setOpenDialog={setOpenDialog}
              />
            </div>
          ) : (
            <>
              <h3 className="text-body-sm text-form-input-disabled">
                {generalIntl?.source_control}
              </h3>
              <div
                className="flex cursor-pointer items-center gap-1 text-link-active"
                onClick={() => setOpenDialog(true)}
              >
                {Icons.Plus('stroke-0')}
                <span className="text-sm">{compIntl?.add_repository}</span>
              </div>
            </>
          )}
          {openDialog && (
            <WorkflowRepoDialog
              component={component}
              repoUrl={repoUrl}
              setRepoUrl={setRepoUrl}
              setRepoLoading={setRepoLoading}
              openDialog={openDialog}
              setOpenDialog={setOpenDialog}
            />
          )}
        </div>
      )}
    </>
  );
};

export default WorkflowRepository;
